import React, { useEffect, useRef, useState } from 'react';
import "./WorkyMainDemoMobile.css";
import { Layers, Users, CreditCard, FileText, BarChart2 } from 'lucide-react';
import { BsStars } from "react-icons/bs";
import { WorkyMainDemoOpsShiftManagementScreen } from './WorkyMainDemoOpsShiftManagementScreen/WorkyMainDemoOpsShiftManagementScreen';
import { WorkyMainDemoOpsWorkforceScreen } from './WorkyMainDemoOpsWorkforceScreen/WorkyMainDemoOpsWorkforceScreen';
import { WorkyMainDemoOpsPayrollScreen } from './WorkyMainDemoOpsPayrollScreen/WorkyMainDemoOpsPayrollScreen';
import { DemoAnimationOverlay } from './DemoAnimationOverlay/DemoAnimationOverlay';
import { ShiftApprovalPanel } from './DemoAnimationOverlay/ShiftApprovalPanel';
import { NewShiftPanel } from './DemoAnimationOverlay/NewShiftPanel';
import { WorkerProfilePanel } from './DemoAnimationOverlay/WorkerProfilePanel';
import { InvoicePdfPanel } from './DemoAnimationOverlay/InvoicePdfPanel';
import { WorkyMainDemoOpsClientInvoicesScreen } from './WorkyMainDemoOpsClientInvoicesScreen/WorkyMainDemoOpsClientInvoicesScreen';
import { useShiftApprovalDemoAnimation, ActiveDemoScreen } from './DemoAnimationOverlay/useShiftApprovalDemoAnimation';
import type { ShiftApprovalWorker, ShiftApprovalShift } from './DemoAnimationOverlay/ShiftApprovalPanel';
import type { NewShiftWorker } from './DemoAnimationOverlay/NewShiftPanel';

// ── Panel data constants ────────────────────────────────────────────────────

const PRIYA_WORKER: ShiftApprovalWorker = {
    name: 'Priya Patel',
    initials: 'PP',
    avatarGradient: 'linear-gradient(135deg, #6d28d9, #a78bfa)',
    role: 'PSW · Healthcare',
};

const PRIYA_SHIFT: ShiftApprovalShift = {
    timeRange: '4PM – 12AM',
    taskName: 'PSW – Memory',
    location: 'Sunnybrook HSC',
    dateRange: 'Mar 20 – Mar 30',
};

const DAVID_WORKER: NewShiftWorker = {
    name: 'David Chen',
    initials: 'DC',
    avatarGradient: 'linear-gradient(135deg, #0e7490, #22d3ee)',
    role: 'Physiotherapist · Healthcare',
};

// ── Component ───────────────────────────────────────────────────────────────

const WorkyMainDemoMobile = (): React.ReactElement => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isInViewport, setIsInViewport] = useState<boolean>(false);

    useEffect((): (() => void) | void => {
        const containerElement: HTMLDivElement | null = containerRef.current;
        if (!containerElement) return undefined;

        const observer: IntersectionObserver = new IntersectionObserver(
            (entries: readonly IntersectionObserverEntry[]): void => {
                const firstEntry: IntersectionObserverEntry | undefined = entries[0];
                setIsInViewport(Boolean(firstEntry?.isIntersecting));
            },
            {
                threshold: 0.1,
            }
        );

        observer.observe(containerElement);

        return (): void => {
            observer.disconnect();
        };
    }, []);

    const animState = useShiftApprovalDemoAnimation(containerRef, isInViewport);

    const isShiftMgmtActive: boolean = animState.activeNavItem === 'shift-management';
    const isWorkforceActive: boolean = animState.activeNavItem === 'workforce';
    const isPayrollActive: boolean = animState.activeNavItem === 'worky-pay';
    const isInvoicesActive: boolean = animState.activeNavItem === 'client-invoices';

    return (
        <div className='wmdm-container' ref={containerRef}>
            {/* ── Phone status bar ───────────────────────────────────── */}
            <div className='wmdm-status-bar'>
                <span className='wmdm-status-time'>9:41</span>
                <div className='wmdm-notch' />
                <div className='wmdm-status-icons'>
                    <svg className='wmdm-signal-icon' viewBox='0 0 18 12' fill='none'>
                        <rect x='0' y='9' width='3' height='3' rx='0.5' fill='currentColor' />
                        <rect x='5' y='6' width='3' height='6' rx='0.5' fill='currentColor' />
                        <rect x='10' y='3' width='3' height='9' rx='0.5' fill='currentColor' />
                        <rect x='15' y='0' width='3' height='12' rx='0.5' fill='currentColor' />
                    </svg>
                    <svg className='wmdm-battery-icon' viewBox='0 0 28 13' fill='none'>
                        <rect x='0.5' y='0.5' width='23' height='12' rx='2.5' stroke='currentColor' strokeWidth='1' />
                        <rect x='2' y='2' width='16' height='9' rx='1' fill='currentColor' />
                        <path d='M25 4.5v4a2 2 0 0 0 0-4Z' fill='currentColor' />
                    </svg>
                </div>
            </div>

            {/* ── Search / AI bar ───────────────────────────────────── */}
            <div className='wmdm-search-bar'>
                <BsStars size={12} />
                <span className='wmdm-search-text'>worky</span>
            </div>

            {/* ── Content area — switches between screens ─────────── */}
            <div className='wmdm-content'>
                {animState.activeScreen === ActiveDemoScreen.WORKFORCE ? (
                    <WorkyMainDemoOpsWorkforceScreen />
                ) : animState.activeScreen === ActiveDemoScreen.PAYROLL ? (
                    <WorkyMainDemoOpsPayrollScreen
                        isFirstCycleExpanded={animState.payrollCycleExpanded}
                    />
                ) : animState.activeScreen === ActiveDemoScreen.INVOICES ? (
                    <WorkyMainDemoOpsClientInvoicesScreen
                        isKingstonExpanded={animState.isKingstonExpanded}
                    />
                ) : (
                    <WorkyMainDemoOpsShiftManagementScreen />
                )}
            </div>

            {/* ── Bottom tab navigation ──────────────────────────────── */}
            <div className='wmdm-bottom-nav'>
                <div
                    data-demo-target='nav-shift-management'
                    className={`wmdm-tab${isShiftMgmtActive ? ' wmdm-tab--active' : ''}`}
                >
                    <Layers size={16} className='wmdm-tab-icon' />
                    <span className='wmdm-tab-label'>Shifts</span>
                </div>

                <div
                    data-demo-target='nav-workforce'
                    className={`wmdm-tab${isWorkforceActive ? ' wmdm-tab--active' : ''}`}
                >
                    <Users size={16} className='wmdm-tab-icon' />
                    <span className='wmdm-tab-label'>Workforce</span>
                </div>

                <div
                    data-demo-target='nav-worky-pay'
                    className={`wmdm-tab${isPayrollActive ? ' wmdm-tab--active' : ''}`}
                >
                    <CreditCard size={16} className='wmdm-tab-icon' />
                    <span className='wmdm-tab-label'>Pay</span>
                </div>

                <div
                    data-demo-target='nav-client-invoices'
                    className={`wmdm-tab${isInvoicesActive ? ' wmdm-tab--active' : ''}`}
                >
                    <FileText size={16} className='wmdm-tab-icon' />
                    <span className='wmdm-tab-label'>Invoices</span>
                </div>

                <div className='wmdm-tab'>
                    <BarChart2 size={16} className='wmdm-tab-icon' />
                    <span className='wmdm-tab-label'>Reports</span>
                </div>
            </div>

            {/* ── Home indicator ─────────────────────────────────────── */}
            <div className='wmdm-home-indicator-area'>
                <div className='wmdm-home-indicator' />
            </div>

            {/* ── Single animation overlay covering the full phone mockup ─── */}
            <DemoAnimationOverlay
                cursorPos={animState.cursorPos}
                cursorTransitionMs={animState.cursorTransitionMs}
                isClicking={animState.isClicking}
                isVisible={animState.isVisible}
            >
                <ShiftApprovalPanel
                    isVisible={animState.showApprovalPopup}
                    worker={PRIYA_WORKER}
                    shift={PRIYA_SHIFT}
                    typedNotes={animState.approvalTypedNotes}
                    isNotesFocused={animState.isApprovalNotesFocused}
                    showSuccess={animState.showApprovalSuccess}
                />

                <NewShiftPanel
                    isVisible={animState.showNewShiftPopup}
                    worker={DAVID_WORKER}
                    dateRange='Mar 30 – Apr 15'
                    isTimeDropdownOpen={animState.isTimeDropdownOpen}
                    selectedTime={animState.selectedTime}
                    isOrgDropdownOpen={animState.isOrgDropdownOpen}
                    selectedOrg={animState.selectedOrg}
                    typedInstructions={animState.newShiftTypedInstructions}
                    isInstructionsFocused={animState.isInstructionsFocused}
                    showSuccess={animState.showNewShiftSuccess}
                />

                <WorkerProfilePanel
                    isVisible={animState.showWorkerProfilePanel}
                    wpIsEditingRate={animState.wpIsEditingRate}
                    wpEditRateText={animState.wpEditRateText}
                    wpFirstRateDisplay={animState.wpFirstRateDisplay}
                    wpDeductionCount={animState.wpDeductionCount}
                />

                <InvoicePdfPanel
                    isVisible={animState.showInvoicePdf}
                    showSuccess={animState.invoiceSentSuccess}
                />
            </DemoAnimationOverlay>

        </div>
    );
};


export { WorkyMainDemoMobile };
